import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { buildChemistryAnalysisCopy } from './chemistryAnalysisCopy';
import {
  CHEMISTRY_ANALYSIS_REDUCED_MS,
  CHEMISTRY_ANALYSIS_TIMELINE,
  CHEMISTRY_ANALYSIS_TOTAL_MS,
  getSceneAtElapsed,
} from './chemistryAnalysisTimeline';
import { getChemistrySequenceDirection, getChemistrySequenceMetrics, getProgressValue } from './chemistryAnalysisUtils';
import './ChemistryAnalysisSequence.css';

function usePrefersReducedMotion() {
  const [prefersReduced, setPrefersReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReduced(Boolean(mediaQuery.matches));
    update();
    mediaQuery.addEventListener?.('change', update);
    return () => mediaQuery.removeEventListener?.('change', update);
  }, []);

  return prefersReduced;
}

function FaceFrame({ image, meshImage, name, accent = 'pink', active = false, lead = false }) {
  const initial = String(name || '?').trim().slice(0, 1).toUpperCase();

  return (
    <div className={`analysis-face-frame accent-${accent} ${active ? 'is-active' : ''} ${lead ? 'is-lead' : ''}`}>
      <div className="analysis-face-media">
        {image ? (
          <img className="analysis-face-original" src={image} alt={name} />
        ) : (
          <div className="analysis-face-fallback" aria-label={name}>{initial}</div>
        )}
        {meshImage ? <img className="analysis-face-mesh" src={meshImage} alt="" aria-hidden="true" /> : null}
        <div className="analysis-landmark-grid" aria-hidden="true" />
        <div className="analysis-scan-line" aria-hidden="true" />
      </div>
      <strong title={name}>{name}</strong>
    </div>
  );
}

function SignalList({ items }) {
  return (
    <ul className="analysis-signal-list">
      {items.map((item) => (
        <li key={item}>
          <span aria-hidden="true" />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function ChemistryAnalysisSequence({
  imageA,
  imageB,
  meshImageA,
  meshImageB,
  nameA,
  nameB,
  result,
  language,
  onComplete,
  onSkip,
}) {
  const { t } = useTranslation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [elapsedMs, setElapsedMs] = useState(0);
  const [isFinishing, setIsFinishing] = useState(false);
  const rafRef = useRef(null);
  const completeTimerRef = useRef(null);
  const startTimeRef = useRef(0);
  const completedRef = useRef(false);

  const metrics = useMemo(() => getChemistrySequenceMetrics(result, language), [result, language]);
  const direction = useMemo(() => getChemistrySequenceDirection(result, nameA, nameB), [result, nameA, nameB]);
  const copy = useMemo(
    () => buildChemistryAnalysisCopy({ result, nameA, nameB, language, t }),
    [result, nameA, nameB, language, t],
  );

  const durationMs = prefersReducedMotion ? CHEMISTRY_ANALYSIS_REDUCED_MS : CHEMISTRY_ANALYSIS_TOTAL_MS;
  const currentScene = prefersReducedMotion ? 'score' : getSceneAtElapsed(elapsedMs)?.id || 'intro';
  const compareScene = CHEMISTRY_ANALYSIS_TIMELINE.find((scene) => scene.id === 'compare');
  const scoreScene = CHEMISTRY_ANALYSIS_TIMELINE.find((scene) => scene.id === 'score');
  const comfortValue = prefersReducedMotion
    ? metrics.comfortScore
    : getProgressValue(elapsedMs, compareScene.start, compareScene.start + Math.round((compareScene.end - compareScene.start) * 0.82), metrics.comfortScore);
  const attractionValue = prefersReducedMotion
    ? metrics.attractionScore
    : getProgressValue(elapsedMs, compareScene.start + 280, compareScene.end, metrics.attractionScore);
  const chemistryValue = prefersReducedMotion
    ? metrics.chemistryScore
    : getProgressValue(elapsedMs, scoreScene.start, scoreScene.start + Math.round((scoreScene.end - scoreScene.start) * 0.86), metrics.chemistryScore);

  const clearTimers = useCallback(() => {
    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (completeTimerRef.current) {
      window.clearTimeout(completeTimerRef.current);
      completeTimerRef.current = null;
    }
  }, []);

  const complete = useCallback(({ immediate = false } = {}) => {
    if (completedRef.current) return;
    completedRef.current = true;
    clearTimers();

    if (immediate) {
      onComplete?.();
      return;
    }

    setIsFinishing(true);
    completeTimerRef.current = window.setTimeout(() => {
      onComplete?.();
    }, prefersReducedMotion ? 80 : 760);
  }, [clearTimers, onComplete, prefersReducedMotion]);

  const handleSkip = useCallback(() => {
    onSkip?.(currentScene);
    complete({ immediate: true });
  }, [complete, currentScene, onSkip]);

  useEffect(() => {
    startTimeRef.current = performance.now();

    const tick = (timestamp) => {
      const nextElapsed = Math.min(timestamp - startTimeRef.current, durationMs);
      setElapsedMs(nextElapsed);
      if (nextElapsed >= durationMs) {
        complete();
        return;
      }
      rafRef.current = window.requestAnimationFrame(tick);
    };

    rafRef.current = window.requestAnimationFrame(tick);

    return clearTimers;
  }, [clearTimers, complete, durationMs]);

  const showScanA = currentScene === 'scanA';
  const showScanB = currentScene === 'scanB';
  const showCompare = currentScene === 'compare';
  const showDirection = currentScene === 'direction';
  const showScore = currentScene === 'score';
  const isAEmphasis = direction.lead === 'a';
  const isBEmphasis = direction.lead === 'b';

  return (
    <section
      className={`analysis-sequence scene-${currentScene} ${isFinishing ? 'is-finishing' : ''} ${prefersReducedMotion ? 'is-reduced-motion' : ''}`}
      aria-label={t('chemistry_result.analysis_sequence.a11y.label')}
    >
      <button type="button" className="analysis-skip-button" onClick={handleSkip}>
        {t('chemistry_result.analysis_sequence.skip')}
      </button>

      <div className="analysis-sequence-inner">
        <div className="analysis-copy-block">
          <span>{copy.intro.eyebrow}</span>
          <h1>{showScore ? copy.score.title : showScanA ? copy.scanA.title : showScanB ? copy.scanB.title : showDirection ? copy.direction.title : copy.intro.title}</h1>
        </div>

        <div className="analysis-face-stage">
          <FaceFrame image={imageA} meshImage={meshImageA} name={nameA} accent="pink" active={showScanA} lead={showDirection && isAEmphasis} />
          <div className="analysis-pair-mark" aria-hidden="true">x</div>
          <FaceFrame image={imageB} meshImage={meshImageB} name={nameB} accent="blue" active={showScanB} lead={showDirection && isBEmphasis} />
          <div className="analysis-connection-wave" aria-hidden="true" />
        </div>

        <div className="analysis-data-stage" aria-live="polite">
          {showScanA ? <SignalList items={copy.scanA.items} /> : null}
          {showScanB ? <SignalList items={copy.scanB.items} /> : null}

          {showCompare ? (
            <div className="analysis-score-grid" aria-label={t('chemistry_result.analysis_sequence.a11y.compare')}>
              <div>
                <span>{copy.compare.comfort}</span>
                <strong>{comfortValue}</strong>
                <p>{copy.compare.comfortLine}</p>
              </div>
              <div>
                <span>{copy.compare.attraction}</span>
                <strong>{attractionValue}</strong>
                <p>{copy.compare.attractionLine}</p>
              </div>
            </div>
          ) : null}

          {showDirection ? (
            <div className={`analysis-direction-card direction-${direction.lead}`}>
              <p>{copy.direction.summary}</p>
              <div aria-hidden="true">
                <span>{direction.flow.a_to_b}</span>
                <i />
                <span>{direction.flow.b_to_a}</span>
              </div>
            </div>
          ) : null}

          {showScore ? (
            <div className="analysis-final-card">
              <div className="analysis-final-score">
                <strong>{chemistryValue}</strong>
                <span>{copy.score.outOf100}</span>
              </div>
              <div>
                <span>{copy.score.gradeCode}</span>
                <h2>{copy.score.gradeLabel}</h2>
                <p>{copy.score.interpretation}</p>
              </div>
            </div>
          ) : null}
        </div>

        <div className="analysis-progress-rail" aria-hidden="true">
          {CHEMISTRY_ANALYSIS_TIMELINE.map((scene) => (
            <span key={scene.id} className={prefersReducedMotion || elapsedMs >= scene.start ? 'is-active' : ''} />
          ))}
        </div>
      </div>
    </section>
  );
}

FaceFrame.propTypes = {
  image: PropTypes.string,
  meshImage: PropTypes.string,
  name: PropTypes.string,
  accent: PropTypes.oneOf(['pink', 'blue']),
  active: PropTypes.bool,
  lead: PropTypes.bool,
};

SignalList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

ChemistryAnalysisSequence.propTypes = {
  imageA: PropTypes.string,
  imageB: PropTypes.string,
  meshImageA: PropTypes.string,
  meshImageB: PropTypes.string,
  nameA: PropTypes.string,
  nameB: PropTypes.string,
  result: PropTypes.object,
  language: PropTypes.string,
  onComplete: PropTypes.func,
  onSkip: PropTypes.func,
};
